import moment from 'moment';

import translate from 'utils/translate';
import parseSesEmail from './parse_ses_email';

const EMAIL_FIELD_KEYS = ['from', 'to', 'replyTo', 'messageId', 'inReplyTo', 'references', 'subject', 'date', 'html', 'text'];
const EMPTY_PARSED_EMAIL = Object.fromEntries(EMAIL_FIELD_KEYS.map((key) => [key, '']));

const extractEmailAddress = (value='') => {
  const matchedEmail = value.match(/<([^>]+)>/);

  if (matchedEmail?.[1]) {
    return matchedEmail[1].trim().toLowerCase();
  }

  return value.trim().toLowerCase();
};

export const getReplySourceEmail = (value) => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const directSource = [
    value.replySourceEmail,
    value.parsedEmail,
    value.sourceEmail,
    value.originalEmail,
  ].find(Boolean);

  if (directSource) {
    return directSource;
  }

  if (!EMAIL_FIELD_KEYS.some((key) => value[key])) {
    return null;
  }

  return {
    ...EMPTY_PARSED_EMAIL,
    ...Object.fromEntries(EMAIL_FIELD_KEYS.map((key) => [key, value[key]])),
  };
};

export const buildReplyDraftFromMessage = async ({message, relationships = {}} = {}) => {
  if (message?.messageType !== 'EMAIL' || message?.body?.editorType === 'MARKDOWN' || !message?.body?.blob) {
    return null;
  }

  const parsedEmail = await parseSesEmail(message.body.blob);
  return buildReplyDraft({
    parsedEmail,
    relationships,
  });
};

const buildReplyDraft = ({parsedEmail = EMPTY_PARSED_EMAIL, relationships = {}} = {}) => {
  const candidateAddresses = [
    parsedEmail.replyTo,
    parsedEmail.from,
    parsedEmail.to,
  ].map(extractEmailAddress).filter(Boolean);

  if (!candidateAddresses.length) {
    return null;
  }

  const relationshipList = Array.isArray(relationships) ? relationships : Object.values(relationships);
  const replyRelationship = relationshipList.find((relationship) => {
    const relationshipEmail = relationship.contacts?.find((contact) => contact.type === 'Email')?.detail ||
      relationship.email;

    return relationshipEmail && candidateAddresses.includes(relationshipEmail.trim().toLowerCase());
  });

  if (!replyRelationship?.relationshipRn) {
    return null;
  }

  const replySubjectPrefix = translate('en', 'client.ClientDetail.Correspondance.Correspondance.ReplyBuilder.SubjectPrefix');
  const replyDateFormat = translate('en', 'client.ClientDetail.Correspondance.Correspondance.ReplyBuilder.DateFormat');
  let replySubject = parsedEmail.subject;
  if (!replySubject.toLowerCase().startsWith(replySubjectPrefix.toLowerCase())) {
    replySubject = replySubject.trim() ? `${replySubjectPrefix} ${replySubject}` : replySubjectPrefix;
  }

  const replyDate = moment(parsedEmail.date);
  const formattedDate = replyDate.isValid() ?
    replyDate.format(replyDateFormat) :
    parsedEmail.date;
  const replyAuthor = parsedEmail.from || parsedEmail.replyTo || parsedEmail.to;
  const replyDateAndAuthor = [formattedDate, replyAuthor].filter(Boolean).join(', ');
  const replyHeader = replyDateAndAuthor ?
    translate('en', 'client.ClientDetail.Correspondance.Correspondance.ReplyBuilder.Header', {
      replyPrefix: replyDateAndAuthor,
    }) :
    translate('en', 'client.ClientDetail.Correspondance.Correspondance.ReplyBuilder.HeaderFallback');
  const referenceMatches = parsedEmail.references.match(/<[^>]+>/g) || [];
  const references = Array.from(new Set([
    ...referenceMatches,
    parsedEmail.messageId,
  ].filter(Boolean)));

  return {
    relationshipRn: replyRelationship.relationshipRn,
    subject: replySubject,
    inReplyTo: parsedEmail.messageId,
    references,
    replyHeader,
    quotedHtml: parsedEmail.html,
    quotedText: parsedEmail.text,
  };
};

export default buildReplyDraft;
